/**
 * InvitesPage
 * Admin page for managing admin invitations
 */

import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { Plus } from "lucide-react";
import { toast } from "sonner";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { PageHeader } from "@/components/common/page-header";
import { CreateInviteDialog } from "@/components/admin/CreateInviteDialog";
import { InviteTable } from "@/components/admin/InviteTable";
import { adminInvitesService } from "@/services/admin-invites.service";
import type { InviteStatus } from "@/types/invite";

export function InvitesPage() {
  const queryClient = useQueryClient();
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [selectedStatus, setSelectedStatus] = useState<InviteStatus | "All">(
    "All",
  );

  // Fetch invites
  const { data: invitesData, isLoading } = useQuery({
    queryKey: ["admin-invites", selectedStatus],
    queryFn: () =>
      adminInvitesService.listInvites(
        selectedStatus !== "All" ? { status: selectedStatus } : undefined,
      ),
  });

  // Revoke invite mutation
  const revokeMutation = useMutation({
    mutationFn: (inviteId: string) =>
      adminInvitesService.revokeInvite(inviteId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["admin-invites"] });
      toast.success("Invitation revoked successfully");
    },
    onError: (error) => {
      console.error("Failed to revoke invite:", error);
      toast.error("Failed to revoke invitation", {
        description:
          error instanceof Error ? error.message : "Please try again",
      });
    },
  });

  // Resend invite mutation
  const resendMutation = useMutation({
    mutationFn: (inviteId: string) =>
      adminInvitesService.resendInvite(inviteId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["admin-invites"] });
      toast.success("Invitation resent successfully");
    },
    onError: (error) => {
      console.error("Failed to resend invite:", error);
      toast.error("Failed to resend invitation", {
        description:
          error instanceof Error ? error.message : "Please try again",
      });
    },
  });

  const handleRevoke = (inviteId: string) => {
    revokeMutation.mutate(inviteId);
  };

  const handleResend = (inviteId: string) => {
    resendMutation.mutate(inviteId);
  };

  const handleCreateSuccess = () => {
    queryClient.invalidateQueries({ queryKey: ["admin-invites"] });
  };

  const invites = invitesData?.data || [];

  return (
    <div className="container mx-auto space-y-6 py-6">
      <PageHeader
        title="Admin Invitations"
        subtitle="Manage admin invitations and access permissions"
        actions={[
          {
            label: "Create Invitation",
            onClick: () => setCreateDialogOpen(true),
            icon: <Plus className="mr-2 h-4 w-4" />,
          },
        ]}
      />

      <Card>
        <CardHeader>
          <CardTitle>Invitations</CardTitle>
          <CardDescription>
            View and manage all admin invitations. You can filter by status and
            revoke pending invitations.
          </CardDescription>
        </CardHeader>
        <CardContent>
          <Tabs
            value={selectedStatus}
            onValueChange={(value) =>
              setSelectedStatus(value as InviteStatus | "All")
            }
          >
            <TabsList className="mb-4">
              <TabsTrigger value="All">All</TabsTrigger>
              <TabsTrigger value="Pending">Pending</TabsTrigger>
              <TabsTrigger value="Accepted">Accepted</TabsTrigger>
              <TabsTrigger value="Expired">Expired</TabsTrigger>
              <TabsTrigger value="Revoked">Revoked</TabsTrigger>
            </TabsList>

            <TabsContent value={selectedStatus} className="mt-0">
              {isLoading ? (
                <div className="flex items-center justify-center py-12">
                  <p className="text-sm text-muted-foreground">
                    Loading invitations...
                  </p>
                </div>
              ) : (
                <InviteTable
                  invites={invites}
                  onRevoke={handleRevoke}
                  onResend={handleResend}
                  isLoading={
                    revokeMutation.isPending || resendMutation.isPending
                  }
                />
              )}
            </TabsContent>
          </Tabs>

          {invitesData && invitesData.pagination?.totalItems > 0 && (
            <div className="mt-4 text-sm text-muted-foreground">
              Showing {invites.length} of {invitesData.pagination?.totalItems}{" "}
              invitation
              {invitesData.pagination?.totalItems !== 1 ? "s" : ""}
            </div>
          )}
        </CardContent>
      </Card>

      <CreateInviteDialog
        open={createDialogOpen}
        onOpenChange={setCreateDialogOpen}
        onSuccess={handleCreateSuccess}
      />
    </div>
  );
}
